import {
  campaignMatchesViewerInterests,
  getCompletionTokenRate,
  normalizeInterestIds
} from './discoveryConfig';

const DB_NAME = 'promogrind-local-stage4';
const DB_VERSION = 3;
const CAMPAIGN_STORE = 'campaigns';
const FEEDBACK_STORE = 'feedback';
const ANALYTICS_STORE = 'analytics';
const GRIND_STORE = 'grinds';

export {
  DEFAULT_FEEDBACK_QUESTION as FEEDBACK_QUESTION,
  FEEDBACK_QUESTION_OPTIONS
} from './discoveryConfig';

export const LOCAL_CAMPAIGN_SOURCE = 'local-indexeddb';
export const FEEDBACK_OPTIONS = ['Yes', 'Maybe', 'No'];
export const DEFAULT_CROP = { x: 50, y: 50, zoom: 1, aspectRatio: '16:9' };

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not available in this browser.'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(CAMPAIGN_STORE)) {
        const campaigns = db.createObjectStore(CAMPAIGN_STORE, { keyPath: 'id' });
        campaigns.createIndex('status', 'status', { unique: false });
        campaigns.createIndex('platform', 'platform', { unique: false });
        campaigns.createIndex('creatorId', 'creatorId', { unique: false });
      }

      if (!db.objectStoreNames.contains(FEEDBACK_STORE)) {
        const feedback = db.createObjectStore(FEEDBACK_STORE, { keyPath: 'id' });
        feedback.createIndex('campaignId', 'campaignId', { unique: false });
        feedback.createIndex('creatorId', 'creatorId', { unique: false });
        feedback.createIndex('campaignViewer', ['campaignId', 'viewerId'], { unique: true });
      }

      if (!db.objectStoreNames.contains(ANALYTICS_STORE)) {
        const analytics = db.createObjectStore(ANALYTICS_STORE, { keyPath: 'id' });
        analytics.createIndex('campaignId', 'campaignId', { unique: false });
        analytics.createIndex('creatorId', 'creatorId', { unique: false });
        analytics.createIndex('type', 'type', { unique: false });
        analytics.createIndex('campaignViewerType', ['campaignId', 'viewerId', 'type'], { unique: false });
      }

      if (!db.objectStoreNames.contains(GRIND_STORE)) {
        const grinds = db.createObjectStore(GRIND_STORE, { keyPath: 'id' });
        grinds.createIndex('campaignId', 'campaignId', { unique: false });
        grinds.createIndex('creatorId', 'creatorId', { unique: false });
        grinds.createIndex('campaignViewer', ['campaignId', 'viewerId'], { unique: true });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function runTransaction(storeName, mode, callback) {
  return openDatabase().then((db) => new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const request = callback(store);

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
  }));
}

function getAllFromStore(storeName) {
  return runTransaction(storeName, 'readonly', (store) => store.getAll());
}

function deleteByKey(storeName, key) {
  return runTransaction(storeName, 'readwrite', (store) => store.delete(key));
}

export async function saveCampaign(campaign) {
  const now = new Date().toISOString();
  const id = campaign.id || `local-campaign-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const record = {
    ...campaign,
    id,
    source: LOCAL_CAMPAIGN_SOURCE,
    createdAt: campaign.createdAt || now,
    updatedAt: now,
    publishedAt: campaign.status === 'Active' ? campaign.publishedAt || now : campaign.publishedAt || null
  };

  await runTransaction(CAMPAIGN_STORE, 'readwrite', (store) => store.put(record));
  return record;
}

export async function getCampaigns() {
  return getAllFromStore(CAMPAIGN_STORE);
}

export async function getCampaignById(campaignId) {
  return runTransaction(CAMPAIGN_STORE, 'readonly', (store) => store.get(campaignId));
}

export async function getActiveCampaignsByPlatform(platform) {
  const campaigns = await getCampaigns();
  return campaigns
    .filter((campaign) => campaign.status === 'Active' && campaign.platform === platform)
    .sort((a, b) => (b.publishedAt || '').localeCompare(a.publishedAt || ''));
}

export async function getActiveCampaignsForDiscovery(viewerInterestIds = []) {
  const campaigns = await getCampaigns();
  return rankCampaignsForDiscovery(
    campaigns.filter((campaign) => campaign.status === 'Active'),
    viewerInterestIds
  );
}

export function normalizeCampaignForDiscovery(campaign, viewerInterestIds = []) {
  const targetInterestIds = normalizeInterestIds(campaign.targetInterestIds);
  const completionTokenRate = campaign.completionTokenRate || getCompletionTokenRate(targetInterestIds);
  const matchesViewerInterests = campaignMatchesViewerInterests(targetInterestIds, viewerInterestIds);

  return {
    ...campaign,
    targetInterestIds,
    completionTokenRate,
    matchesViewerInterests,
    discoveryAudienceMode: targetInterestIds.length > 0 ? 'targeted' : 'legacy'
  };
}

export function rankCampaignsForDiscovery(campaigns = [], viewerInterestIds = []) {
  return campaigns
    .map((campaign) => normalizeCampaignForDiscovery(campaign, viewerInterestIds))
    .sort((a, b) => {
      if (a.matchesViewerInterests !== b.matchesViewerInterests) {
        return a.matchesViewerInterests ? -1 : 1;
      }

      if (a.discoveryAudienceMode !== b.discoveryAudienceMode) {
        return a.discoveryAudienceMode === 'targeted' ? -1 : 1;
      }

      return (b.publishedAt || b.createdAt || '').localeCompare(a.publishedAt || a.createdAt || '');
    });
}

export async function getCampaignsForCreator(creatorId) {
  const campaigns = await getCampaigns();
  return campaigns
    .filter((campaign) => campaign.creatorId === creatorId)
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

export async function deleteCampaignForCreator(campaignId, creatorId) {
  const campaign = await getCampaignById(campaignId);
  if (!campaign) return false;
  if (campaign.creatorId !== creatorId) {
    throw new Error('This local campaign does not belong to the current creator.');
  }

  const feedback = await getFeedbackForCampaign(campaignId).catch(() => []);
  const analytics = await getAnalyticsForCampaign(campaignId).catch(() => []);
  const grinds = await getGrindsForCampaign(campaignId).catch(() => []);

  await deleteByKey(CAMPAIGN_STORE, campaignId);
  await Promise.all([
    ...feedback.map((entry) => deleteByKey(FEEDBACK_STORE, entry.id)),
    ...analytics.map((entry) => deleteByKey(ANALYTICS_STORE, entry.id)),
    ...grinds.map((entry) => deleteByKey(GRIND_STORE, entry.id))
  ]);

  return true;
}

export async function saveFeedback(feedback) {
  const now = new Date().toISOString();
  const id = feedback.id || `${feedback.campaignId}:${feedback.viewerId}`;
  const record = {
    ...feedback,
    id,
    createdAt: feedback.createdAt || now
  };

  await runTransaction(FEEDBACK_STORE, 'readwrite', (store) => store.add(record));
  return record;
}

export async function getFeedback() {
  return getAllFromStore(FEEDBACK_STORE);
}

export async function getFeedbackForCampaign(campaignId) {
  const feedback = await getFeedback();
  return feedback
    .filter((entry) => entry.campaignId === campaignId)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export async function hasFeedbackFromViewer(campaignId, viewerId) {
  const feedback = await getFeedbackForCampaign(campaignId);
  return feedback.some((entry) => entry.viewerId === viewerId);
}

export async function saveGrind(grind) {
  const now = new Date().toISOString();
  const id = grind.id || `${grind.campaignId}:${grind.viewerId}`;
  const record = {
    ...grind,
    id,
    type: 'grind',
    createdAt: grind.createdAt || now
  };

  await runTransaction(GRIND_STORE, 'readwrite', (store) => store.add(record));
  await saveAnalyticsEvent({
    id: `grind:${grind.campaignId}:${grind.viewerId}`,
    type: 'grind',
    campaignId: grind.campaignId,
    creatorId: grind.creatorId,
    campaignTitle: grind.campaignTitle,
    viewerId: grind.viewerId,
    platform: grind.platform
  });
  return record;
}

export async function getGrinds() {
  return getAllFromStore(GRIND_STORE);
}

export async function getGrindsForCampaign(campaignId) {
  const grinds = await getGrinds();
  return grinds
    .filter((entry) => entry.campaignId === campaignId)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export async function hasGrindFromViewer(campaignId, viewerId) {
  const grinds = await getGrindsForCampaign(campaignId);
  return grinds.some((entry) => entry.viewerId === viewerId);
}

async function saveAnalyticsEvent(event) {
  const now = new Date().toISOString();
  const record = {
    ...event,
    createdAt: event.createdAt || now
  };

  await runTransaction(ANALYTICS_STORE, 'readwrite', (store) => store.put(record));
  return record;
}

export function createAnalyticsSessionId() {
  return `analytics-session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function recordCampaignView({ campaignId, creatorId, campaignTitle, viewerId, platform, sessionId }) {
  return saveAnalyticsEvent({
    id: `view:${campaignId}:${viewerId}:${sessionId}`,
    type: 'view',
    campaignId,
    creatorId,
    campaignTitle,
    viewerId,
    platform,
    sessionId
  });
}

export async function recordCampaignCompletion({ campaignId, creatorId, campaignTitle, viewerId, platform, feedbackId }) {
  return saveAnalyticsEvent({
    id: `completion:${campaignId}:${viewerId}`,
    type: 'completion',
    campaignId,
    creatorId,
    campaignTitle,
    viewerId,
    platform,
    feedbackId
  });
}

export async function getAnalyticsForCampaign(campaignId) {
  const analytics = await getAllFromStore(ANALYTICS_STORE);
  return analytics
    .filter((entry) => entry.campaignId === campaignId)
    .sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export function getCampaignAnalyticsTotals(analytics = [], feedback = [], grinds = []) {
  const views = analytics.filter((entry) => entry.type === 'view').length;
  const trackedCompletions = analytics.filter((entry) => entry.type === 'completion').length;
  const trackedGrinds = analytics.filter((entry) => entry.type === 'grind').length;

  return {
    views,
    grinds: Math.max(trackedGrinds, grinds.length),
    completedDiscoveries: Math.max(trackedCompletions, feedback.length)
  };
}

export function getFeedbackTotals(feedback) {
  return FEEDBACK_OPTIONS.reduce((totals, option) => {
    totals[option] = feedback.filter((entry) => entry.answer === option).length;
    return totals;
  }, {});
}
